import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { ArrowLeft, Send, CheckCircle, AlertCircle, Upload } from 'lucide-react';

const VehicleInspectionForm = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  
  const [formData, setFormData] = useState({
    vehicle_id: '',
    date_of_inspection: new Date().toISOString().split('T')[0],
    location_of_inspection: '',
    assigned_user: '',
    additional_notes: '',
    driver_signature: '',
    supervisor_signature: '',
    status: 'Completed'
  });

  // Inspection categories with bilingual labels
  const exteriorItems = [
    { key: 'body_damage', en: 'Body Damage', es: 'Daños a la carrocería' },
    { key: 'headlights', en: 'Headlights', es: 'Luces delanteras' },
    { key: 'taillights', en: 'Taillights', es: 'Luces traseras' },
    { key: 'brake_lights', en: 'Brake Lights', es: 'Luces de freno' },
    { key: 'turn_signals', en: 'Turn Signals', es: 'Direccionales' },
    { key: 'tires', en: 'Tires – Condition & Pressure', es: 'Gomas – Condición y presión' },
    { key: 'windshield', en: 'Windshield & Wipers', es: 'Parabrisas y limpiaparabrisas' },
    { key: 'mirrors', en: 'Mirrors', es: 'Espejos' }
  ];

  const interiorItems = [
    { key: 'seat_belts', en: 'Seat Belts', es: 'Cinturones de seguridad' },
    { key: 'horn', en: 'Horn', es: 'Bocina' },
    { key: 'dashboard_lights', en: 'Dashboard Warning Lights', es: 'Luces de advertencia' },
    { key: 'cleanliness', en: 'Interior Cleanliness', es: 'Limpieza interior' },
    { key: 'ac', en: 'A/C Functionality', es: 'Aire acondicionado' },
    { key: 'radio', en: 'Radio (if applicable)', es: 'Radio (si aplica)' }
  ];

  const fluidsItems = [
    { key: 'engine_oil', en: 'Engine Oil Level', es: 'Nivel de aceite' },
    { key: 'coolant', en: 'Coolant Level', es: 'Nivel de coolant' },
    { key: 'brake_fluid', en: 'Brake Fluid', es: 'Fluido de frenos' },
    { key: 'power_steering', en: 'Power Steering Fluid', es: 'Fluido de dirección' },
    { key: 'washer_fluid', en: 'Windshield Washer Fluid', es: 'Fluido de limpiaparabrisas' }
  ];

  const safetyItems = [
    { key: 'fire_extinguisher', en: 'Fire Extinguisher', es: 'Extintor' },
    { key: 'first_aid', en: 'First Aid Kit', es: 'Botiquín' },
    { key: 'safety_cones', en: 'Safety Cones', es: 'Conos de seguridad' },
    { key: 'flashlight', en: 'Flashlight', es: 'Linterna' }
  ];

  const mechanicalItems = [
    { key: 'brakes', en: 'Brakes', es: 'Frenos' },
    { key: 'steering', en: 'Steering', es: 'Dirección' },
    { key: 'suspension', en: 'Suspension', es: 'Suspensión' },
    { key: 'transmission', en: 'Transmission Shifting', es: 'Transmisión' },
    { key: 'engine', en: 'Engine Performance', es: 'Motor' }
  ];

  const [inspectionData, setInspectionData] = useState({
    exterior: {},
    interior: {},
    fluids: {},
    safety: {},
    mechanical: {}
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data.filter(v => v.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch vehicles');
    }
  };

  const handleVehicleSelect = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setSelectedVehicle(vehicle);
    setFormData({ ...formData, vehicle_id: vehicleId });
  };

  const handleInspectionChange = (category, itemKey, field, value) => {
    setInspectionData({
      ...inspectionData,
      [category]: {
        ...inspectionData[category],
        [itemKey]: {
          ...inspectionData[category][itemKey],
          [field]: value
        }
      }
    });
  };

  const getInspectionStatus = (category, itemKey) => {
    return inspectionData[category]?.[itemKey]?.status || 'satisfactory';
  };

  const getInspectionDescription = (category, itemKey) => {
    return inspectionData[category]?.[itemKey]?.description || '';
  };

  const buildInspectionItems = (category, items) => {
    return items.map(item => ({
      category: category,
      item: `${item.en} / ${item.es}`,
      status: inspectionData[category]?.[item.key]?.status || 'satisfactory',
      description: inspectionData[category]?.[item.key]?.description || null,
      photo: inspectionData[category]?.[item.key]?.photo || null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that items marked "needs_attention" have descriptions
    let hasIssues = false;
    Object.entries(inspectionData).forEach(([category, items]) => {
      Object.entries(items).forEach(([key, item]) => {
        if (item.status === 'needs_attention' && !item.description) {
          hasIssues = true;
        }
      });
    });

    if (hasIssues) {
      toast.error('Please provide descriptions for all items marked "Needs Attention"');
      return;
    }

    setLoading(true);

    try {
      const inspectionPayload = {
        vehicle_id: formData.vehicle_id,
        date_of_inspection: formData.date_of_inspection,
        location_of_inspection: formData.location_of_inspection,
        assigned_user: formData.assigned_user || null,
        exterior_items: buildInspectionItems('exterior', exteriorItems),
        interior_items: buildInspectionItems('interior', interiorItems),
        fluids_items: buildInspectionItems('fluids', fluidsItems),
        safety_equipment_items: buildInspectionItems('safety', safetyItems),
        mechanical_items: buildInspectionItems('mechanical', mechanicalItems),
        additional_notes: formData.additional_notes || null,
        driver_signature: formData.driver_signature,
        supervisor_signature: formData.supervisor_signature || null,
        status: formData.status
      };

      await axios.post(`${API}/vehicle-inspections`, inspectionPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Vehicle inspection submitted successfully!');
      navigate('/field/vehicle-inspections');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inspection');
    } finally {
      setLoading(false);
    }
  };

  const renderInspectionCategory = (title, titleEs, category, items) => (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg border-b pb-2">{title} / {titleEs}</h3>
      {items.map((item) => (
        <div key={item.key} className="p-4 bg-slate-50 rounded-lg border">
          <div className="flex items-start justify-between mb-3">
            <Label className="font-medium">{item.en} / {item.es}</Label>
            <RadioGroup
              value={getInspectionStatus(category, item.key)}
              onValueChange={(value) => handleInspectionChange(category, item.key, 'status', value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="satisfactory" id={`${category}-${item.key}-sat`} />
                <Label htmlFor={`${category}-${item.key}-sat`} className="font-normal flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Satisfactory / Satisfactorio</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="needs_attention" id={`${category}-${item.key}-att`} />
                <Label htmlFor={`${category}-${item.key}-att`} className="font-normal flex items-center gap-1">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">Needs Attention / Necesita Atención</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {getInspectionStatus(category, item.key) === 'needs_attention' && (
            <div className="mt-3 space-y-3 pl-4 border-l-2 border-orange-400">
              <div>
                <Label className="text-sm text-orange-700">Description Required / Descripción Requerida *</Label>
                <Textarea
                  value={getInspectionDescription(category, item.key)}
                  onChange={(e) => handleInspectionChange(category, item.key, 'description', e.target.value)}
                  placeholder="Describe the issue / Describa el problema"
                  rows={2}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">
                  <Upload className="h-4 w-4 inline mr-1" />
                  Photo Upload (Optional)
                </Label>
                <p className="text-xs text-slate-500">Photo upload feature placeholder</p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <Button
        variant="outline"
        onClick={() => navigate('/field')}
        className="mb-4 bg-white"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back / Volver
      </Button>

      <Card className="shadow-xl">
        <div className="h-4" style={{ backgroundColor: '#FFCBA4' }} />
        <CardHeader>
          <CardTitle className="text-2xl">
            VEHICLE INSPECTION / INSPECCIÓN DE VEHÍCULO
          </CardTitle>
          <CardDescription>
            Weekly standardized inspection / Inspección estandarizada semanal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Vehicle Selection */}
            <div className="space-y-4 p-4 bg-orange-50 rounded-lg">
              <h3 className="font-semibold">Vehicle Information / Información del Vehículo</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vehicle">Vehicle / Vehículo *</Label>
                  <select
                    id="vehicle"
                    data-testid="vi-vehicle-select"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.vehicle_id}
                    onChange={(e) => handleVehicleSelect(e.target.value)}
                    required
                  >
                    <option value="">Select Vehicle / Seleccionar Vehículo</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        #{vehicle.vehicle_number} - {vehicle.tag}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedVehicle && (
                  <div className="space-y-2">
                    <Label>Vehicle Description / Descripción</Label>
                    <Input
                      value={selectedVehicle.description || 'N/A'}
                      disabled
                      className="bg-white"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="date">Date of Inspection / Fecha *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date_of_inspection}
                    onChange={(e) => setFormData({ ...formData, date_of_inspection: e.target.value })}
                    required
                    data-testid="vi-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location / Ubicación *</Label>
                  <Input
                    id="location"
                    value={formData.location_of_inspection}
                    onChange={(e) => setFormData({ ...formData, location_of_inspection: e.target.value })}
                    placeholder="Inspection location"
                    required
                    data-testid="vi-location-input"
                  />
                </div>
              </div>
            </div>

            {/* Inspection Categories */}
            {formData.vehicle_id && (
              <>
                {renderInspectionCategory('EXTERIOR', 'EXTERIOR', 'exterior', exteriorItems)}
                {renderInspectionCategory('INTERIOR', 'INTERIOR', 'interior', interiorItems)}
                {renderInspectionCategory('FLUIDS', 'FLUIDOS', 'fluids', fluidsItems)}
                {renderInspectionCategory('SAFETY EQUIPMENT', 'EQUIPO DE SEGURIDAD', 'safety', safetyItems)}
                {renderInspectionCategory('MECHANICAL OBSERVATIONS', 'OBSERVACIONES MECÁNICAS', 'mechanical', mechanicalItems)}

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes / Notas Adicionales</Label>
                  <Textarea
                    id="notes"
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    rows={4}
                    placeholder="Any additional comments / Comentarios adicionales"
                    data-testid="vi-notes-input"
                  />
                </div>

                {/* Status Selection */}
                <div className="space-y-2">
                  <Label htmlFor="status">Status / Estado *</Label>
                  <select
                    id="status"
                    className="w-full border rounded-md px-3 py-2"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    required
                    data-testid="vi-status-select"
                  >
                    <option value="Completed">Completed / Completado</option>
                    <option value="Needs Follow-Up">Needs Follow-Up / Requiere seguimiento</option>
                  </select>
                </div>

                {/* Signatures */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="driver_sig">Driver/Technician Signature / Firma del Conductor *</Label>
                    <Input
                      id="driver_sig"
                      value={formData.driver_signature}
                      onChange={(e) => setFormData({ ...formData, driver_signature: e.target.value })}
                      placeholder="Type your full name / Nombre completo"
                      required
                      data-testid="vi-driver-signature-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="supervisor_sig">Supervisor Signature / Firma del Supervisor (Optional)</Label>
                    <Input
                      id="supervisor_sig"
                      value={formData.supervisor_signature}
                      onChange={(e) => setFormData({ ...formData, supervisor_signature: e.target.value })}
                      placeholder="Supervisor name / Nombre del supervisor"
                      data-testid="vi-supervisor-signature-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="vi-submit-btn"
                  style={{ backgroundColor: '#6B9F3E' }}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Submitting... / Enviando...' : 'Submit Inspection / Enviar Inspección'}
                </Button>
              </>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default VehicleInspectionForm;
